import { useMemo, useState, type FormEvent } from 'react'
import { PROMO_NOTE_MAX, type PromoCode } from '@manaro/contracts'
import { ApiError } from '@/shared/api/client.ts'
import { Button } from '@/shared/ui/button.tsx'
import { Field } from '@/shared/ui/field.tsx'
import { Alert } from '@/shared/ui/states.tsx'
import { limitsFormOf, parseLimitsForm, toDateInput, type LimitsForm } from '../form.ts'
import { promoTerms } from '../labels.ts'
import { useUpdatePromoCode } from '../use-promo-codes.ts'
import { DialogActions, FieldHint, PromoDialog } from './promo-dialog.tsx'

const numeric = (value: string): string => value.replace(/\D/g, '')

/** Limit, last day and note; the terms stay as they were given out. */
export function LimitsDialog({ promo, onClose }: { promo: PromoCode; onClose: () => void }) {
  const today = useMemo(() => toDateInput(new Date()), [])
  const initial = useMemo(() => limitsFormOf(promo), [promo])
  const [form, setForm] = useState<LimitsForm>(initial)
  const [validationError, setValidationError] = useState<string | null>(null)
  const save = useUpdatePromoCode()

  const update = (patch: Partial<LimitsForm>) => {
    setForm((current) => ({ ...current, ...patch }))
    setValidationError(null)
  }

  const dirty = form.maxRedemptions !== initial.maxRedemptions || form.expiresAt !== initial.expiresAt || form.note !== initial.note
  const limitBelowUsed = form.maxRedemptions !== '' && Number(form.maxRedemptions) <= promo.used

  const error =
    validationError ??
    (save.error ? (save.error instanceof ApiError ? save.error.message : 'Не получилось — попробуйте ещё раз.') : null)

  return (
    <PromoDialog
      titleId="promo-limits-title"
      title="Лимит и срок"
      subtitle={
        <>
          <span className="font-mono text-ink">{promo.code}</span> · {promoTerms(promo)}
        </>
      }
      busy={save.isPending}
      dirty={dirty}
      onClose={onClose}
    >
      {(close) => {
        function submit(event: FormEvent) {
          event.preventDefault()
          if (save.isPending) return

          const parsed = parseLimitsForm(form, promo, today)
          if (parsed === null) {
            close()
            return
          }
          if (!parsed.ok) {
            setValidationError(parsed.error)
            return
          }
          save.mutate({ id: promo.id, request: parsed.request }, { onSuccess: close })
        }

        return (
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label="Сколько раз можно ввести"
                inputMode="numeric"
                value={form.maxRedemptions}
                onChange={(event) => update({ maxRedemptions: numeric(event.target.value).slice(0, 6) })}
                placeholder="Без лимита"
                className="max-md:text-[16px]"
              />
              <Field
                label="Последний день"
                type="date"
                min={today}
                value={form.expiresAt}
                onChange={(event) => update({ expiresAt: event.target.value })}
                className="max-md:text-[16px]"
              />
            </div>
            <FieldHint>
              Уже ввели: {promo.used}. Пустые поля — без лимита и бессрочно.
              {limitBelowUsed && ' С таким лимитом код сразу станет исчерпан — новые салоны его не введут.'}
            </FieldHint>

            <label className="flex flex-col gap-1.5">
              <span className="flex items-baseline justify-between gap-2.5">
                <span className="text-[13px] font-medium text-ink-soft">Заметка для команды</span>
                <span className="tabular text-[12px] text-ink-faint">
                  {form.note.length}/{PROMO_NOTE_MAX}
                </span>
              </span>
              <textarea
                value={form.note}
                onChange={(event) => update({ note: event.target.value })}
                rows={2}
                maxLength={PROMO_NOTE_MAX}
                className="resize-y rounded-control border border-line-strong bg-surface-soft px-3.5 py-2.5 text-[16px] text-ink outline-none placeholder:text-ink-muted focus:border-brand focus:shadow-[0_0_0_3px_var(--color-brand-ring)] md:text-[14.5px]"
              />
            </label>

            {error && <Alert>{error}</Alert>}

            <DialogActions>
              <Button variant="secondary" size="sm" disabled={save.isPending} onClick={close}>
                Отмена
              </Button>
              <Button type="submit" size="sm" loading={save.isPending}>
                Сохранить
              </Button>
            </DialogActions>
          </form>
        )
      }}
    </PromoDialog>
  )
}
